// Inbound & Outbound traffic reports. Inbound: per-queue offered/answered/
// abandoned + queue/IVR/ring/hold/talk/wrap timings + SLA. Outbound: per
// campaign+dial-mode attempts/connects/connect-rate + talk/wrap.
import { useEffect, useMemo, useState } from "react";
import {
  PhoneIncoming, PhoneOutgoing, Loader2, Download, FileSpreadsheet, Search,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MultiSelect, isoToday } from "./_daterange";

export default function TrafficReportsPage() {
  const [tab, setTab] = useState("inbound");
  const [opts, setOpts] = useState({ campaigns: [], queues: [], agents: [] });
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoToday());
  const [queueIds, setQueueIds] = useState([]);
  const [campaignIds, setCampaignIds] = useState([]);
  const [agentIds, setAgentIds] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState("");

  useEffect(() => {
    api.get("/reports/call-detail/filters")
      .then((r) => setOpts(r.data))
      .catch((e) => toast.error(formatErr(e)));
  }, []);

  const body = useMemo(() => {
    const b = { date_from: from, date_to: to };
    if (agentIds.length) b.agent_ids = agentIds;
    if (tab === "inbound") { if (queueIds.length) b.queue_ids = queueIds; }
    else { if (campaignIds.length) b.campaign_ids = campaignIds; }
    return b;
  }, [tab, from, to, queueIds, campaignIds, agentIds]);

  async function run() {
    setBusy(true);
    try {
      const r = await api.post(`/reports/${tab}/run`, body);
      setData(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { setData(null); run(); /* eslint-disable-next-line */ }, [tab]);

  async function exportFile(fmt) {
    setDl(fmt);
    try {
      const r = await api.post(`/reports/${tab}/export?fmt=${fmt}`, body, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tab}-report-${from}_to_${to}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setDl(""); }
  }

  const columns = data?.columns || [];
  const rows = data?.rows || [];

  return (
    <div className="px-4 sm:px-6 py-6" data-testid="traffic-reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Inbound & Outbound Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Contact-centre traffic KPIs. Inbound = queue/IVR/ring/SLA per queue; Outbound = attempts/connects per campaign & dial mode.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => exportFile("csv")} disabled={!rows.length || dl} className="gap-1.5" data-testid="tr-export-csv">
            {dl === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} CSV
          </Button>
          <Button onClick={() => exportFile("xlsx")} disabled={!rows.length || dl} className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="tr-export-xlsx">
            {dl === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-border" data-testid="tr-tabs">
        {[
          { id: "inbound", label: "Inbound", Icon: PhoneIncoming },
          { id: "outbound", label: "Outbound", Icon: PhoneOutgoing },
        ].map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`tr-tab-${id}`}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors
              ${tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="tr-filters">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">From</label>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="tr-date-from" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">To</label>
          <input type="date" value={to} min={from} max={isoToday()} onChange={(e) => setTo(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="tr-date-to" />
        </div>
        {tab === "inbound"
          ? <MultiSelect label="Queue" options={opts.queues} selected={queueIds} onChange={setQueueIds} testid="tr-filter-queue" />
          : <MultiSelect label="Campaign" options={opts.campaigns} selected={campaignIds} onChange={setCampaignIds} testid="tr-filter-campaign" />}
        <MultiSelect label="Agent" options={opts.agents} selected={agentIds} onChange={setAgentIds} testid="tr-filter-agent" />
        <Button onClick={run} disabled={busy} className="gap-1.5 h-9" data-testid="tr-apply">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Apply
        </Button>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden" data-testid="tr-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-muted/60">
              <tr>
                {columns.map((c, i) => (
                  <th key={c.id} className={`px-3 py-2 font-semibold text-[11px] uppercase tracking-wider ${i === 0 ? "text-left" : "text-right"}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={columns.length || 1} className="px-3 py-10 text-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin inline" /> Loading…
                </td></tr>
              )}
              {!busy && rows.length === 0 && (
                <tr><td colSpan={columns.length || 1} className="px-3 py-10 text-center text-muted-foreground italic">
                  No {tab} activity for these filters.
                </td></tr>
              )}
              {!busy && rows.map((r, ri) => (
                <tr key={ri} className="border-t border-border/50 hover:bg-muted/20" data-testid={`tr-row-${ri}`}>
                  {columns.map((c, i) => (
                    <td key={c.id} className={`px-3 py-1.5 ${i === 0 ? "text-left font-medium" : "text-right font-mono"}`}>
                      {String(r[c.id] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
